import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Mail, Lock, LogIn, Eye, EyeOff, Zap, TrendingUp, Users, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import supabase from '@/lib/customSupabaseClient';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, user, profile, loading: authLoading, signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  useEffect(() => {
    if (!authLoading && user && profile && loginAttempted) {
      const userRole = profile.role;
      if (userRole === 'system_admin' || userRole === 'admin') {
        toast({ title: '🎉 Platform access', description: 'Opening platform console (add companies)…', variant: 'success' });
        setTimeout(() => { navigate('/admin/dashboard', { replace: true }); }, 800);
      } else if (userRole === 'owner' || userRole === 'supervisor') {
        toast({ title: '🎉 Company access', description: 'Opening your company dashboard…', variant: 'success' });
        setTimeout(() => { navigate('/company', { replace: true }); }, 800);
      } else {
        toast({ title: '🚫 Access Denied', description: `Role (${userRole}) is not admin. Techs use the main app login.`, variant: 'destructive' });
        signOut();
        setLoginAttempted(false);
      }
    }
  }, [user, profile, authLoading, navigate, toast, signOut, loginAttempted]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: '⚠️ Missing Information', description: 'Please enter both email and password to continue.', variant: 'destructive' });
      return;
    }
    if (attemptCount >= 5) {
      toast({ title: '🔒 Too Many Attempts', description: 'Please wait 5 minutes before trying again for security reasons.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    setLoginAttempted(true);
    setAttemptCount(prev => prev + 1);
    try {
      const { data, error } = await signIn(email, password, { silent: true });
      if (error) throw new Error(error.message);
      if (!data?.user) throw new Error('Authentication failed - no user data received');
    } catch (error) {
      console.error('Admin login error:', error);
      let errorMessage = 'Invalid login credentials';
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'The email or password you entered is incorrect. Please try again.';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Please check your email and confirm your account before logging in.';
      } else if (error.message.includes('Too many requests')) {
        errorMessage = 'Too many login attempts. Please wait a moment before trying again.';
      }
      toast({ title: '❌ Authentication Failed', description: errorMessage, variant: 'destructive' });
      setLoginAttempted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast({ title: '⚠️ Email Required', description: 'Please enter your email address.', variant: 'destructive' });
      return;
    }
    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: 'https://tradetitans.pro/reset-password',
      });
      if (error) throw error;
      toast({ title: '📧 Reset Link Sent', description: 'Check your email for a password reset link. It may take a few minutes to arrive.', variant: 'success' });
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast({ title: '❌ Failed to Send', description: 'Could not send reset email. Please try again.', variant: 'destructive' });
    } finally {
      setIsSendingReset(false);
    }
  };

  const finalLoadingState = isSubmitting || authLoading;

  const features = [
    { icon: Zap, title: "Lightning Fast", description: "Real-time updates and instant notifications" },
    { icon: TrendingUp, title: "Analytics Dashboard", description: "Comprehensive insights and reporting" },
    { icon: Users, title: "Team Management", description: "Manage your entire field force efficiently" },
    { icon: Award, title: "Enterprise Grade", description: "Bank-level security and reliability" }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-red-900 to-slate-800 p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="flex items-center justify-center">
          <Card className="w-full max-w-md bg-slate-800/90 backdrop-blur-lg border-slate-700 shadow-2xl shadow-red-500/20">
            <CardHeader className="text-center">
              <motion.div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-500 to-orange-600 rounded-full flex items-center justify-center mb-4 shadow-lg" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Shield className="w-8 h-8 text-white" />
              </motion.div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Admin Command Center</CardTitle>
              <CardDescription className="text-slate-400">Unlock the full power of TradeTitans Field Forces</CardDescription>
            </CardHeader>
            <CardContent>
              <AnimatePresence mode="wait">
                {!showForgotPassword ? (
                  <motion.div key="admin-login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <form onSubmit={handleAdminLogin} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="email" type="email" placeholder="admin@yourcompany.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={finalLoadingState} className="pl-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-red-500 focus:border-red-500 transition-all duration-200" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-slate-300">Password</Label>
                          <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-red-400 hover:text-red-300 hover:underline transition-colors">
                            Forgot Password?
                          </button>
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={finalLoadingState} className="pl-10 pr-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-red-500 focus:border-red-500 transition-all duration-200" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors" aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <AnimatePresence>
                        {attemptCount > 0 && attemptCount < 5 && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="text-xs text-orange-400 bg-orange-900/20 p-2 rounded">
                            Login attempt {attemptCount} of 5
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white py-3 text-base font-semibold transition-all duration-200 transform hover:scale-105" disabled={finalLoadingState}>
                        {finalLoadingState ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />) : (<LogIn className="w-5 h-5 mr-2" />)}
                        {finalLoadingState ? 'Authenticating...' : 'Access Command Center'}
                      </Button>
                    </form>
                    <div className="mt-6 text-center">
                      <p className="text-sm text-slate-400">Regular user?{' '}<Link to="/login" className="font-medium text-purple-400 hover:underline hover:text-purple-300 transition-colors">Go to user login</Link></p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="admin-forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <form onSubmit={handleForgotPassword} className="space-y-6">
                      <div className="text-center mb-2">
                        <h3 className="text-lg font-semibold text-white">Reset Admin Password</h3>
                        <p className="text-sm text-slate-400 mt-1">Enter your admin email and we'll send you a reset link.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email" className="text-slate-300">Admin Email Address</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="forgot-email" type="email" placeholder="admin@yourcompany.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required disabled={isSendingReset} className="pl-10 bg-slate-700 border-slate-600 text-white placeholder-slate-500 focus:ring-red-500 focus:border-red-500 transition-all duration-200" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white py-3 text-base font-semibold transition-all duration-200 transform hover:scale-105" disabled={isSendingReset}>
                        {isSendingReset ? (<motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />) : (<Mail className="w-5 h-5 mr-2" />)}
                        {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                    </form>
                    <div className="mt-4 text-center">
                      <button type="button" onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }} className="text-sm text-slate-400 hover:text-slate-200 transition-colors">
                        ← Back to Admin Login
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex items-center justify-center">
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white mb-4">Transform Your Field Operations</h2>
              <p className="text-xl text-slate-300 mb-8">Join thousands of field service professionals who trust TradeTitans to manage their operations</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }} className="bg-slate-800/50 backdrop-blur-lg border border-slate-700 rounded-lg p-6 hover:bg-slate-800/70 transition-all duration-200">
                  <feature.icon className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }} className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">🚀 Get the Mobile App</h3>
              <p className="text-slate-300 mb-4">Take your field operations on the go with our native mobile app</p>
              <div className="flex justify-center space-x-4">
                <Button className="bg-black hover:bg-gray-800 text-white">📱 App Store</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white">🤖 Google Play</Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
